import { type Maybe } from '@adornis/base/utilTypes.js';
import { type Appointment } from '@adornis/buchungsheld-db/entities/appointment.js';
import { WorkshopDocument } from '@adornis/buchungsheld-db/entities/WorkshopDocument.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import type { DialogOptions } from '@adornis/dialog/x-plain-dialog.js';
import { askForFile } from '@adornis/file-utils/client/helper-methods.js';
import '@adornis/file-utils/client/x-file-input.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { filter } from 'rxjs';

@customElement('bh-prompt-workshop-document-modify')
export class BHPromptWorkshopDocumentModify extends TrDialog<void> {
  @property() mode: Maybe<'add' | 'edit'> = undefined;
  @property({ attribute: false }) workshopDocument = new RXController<Maybe<WorkshopDocument>>(this, undefined);

  @state() private readonly _form = new FormController(this, this.workshopDocument.observable.pipe(filter(Boolean)), {
    success: async doc => {
      return this.close();
    },
    error: err => {
      console.log(err);
    },
  });

  @state() private _file?: File;

  override render() {
    if (!this._form.document) return html` <x-loading-state></x-loading-state> `;

    return html`
      <x-flex space="md">
        ${this.mode === 'add'
          ? html`<x-h2>${this._translation.translate('PROMPT', 'add_workshop_document')}</x-h2>`
          : html`<x-h2>${this._translation.translate('PROMPT', 'edit_workshop_document')}</x-h2>`}

        <x-input ${this._form.field('title')} placeholder="Titel"></x-input>
        <x-textarea ${this._form.field('text')} placeholder="Beschreibung"></x-textarea>

        ${this._form.document.fileID || this._file
          ? html`
              <x-flex horizontal space="sm">
                <tr-button-small flex icon="check" .color=${this.colors.tone.success} not-clickable>
                  <x-text>${this._translation.translate('PROMPT', 'file_uploaded')}</x-text>
                </tr-button-small>
                <tr-button-small
                  icon="delete"
                  .color=${this.colors.tone.error}
                  .action=${() => {
                    if (!this._form.document) return;

                    this._form.document.fileID = '';
                    this._file = undefined;
                    this.requestUpdate();
                  }}
                ></tr-button-small>
              </x-flex>
            `
          : html`
              <x-button
                @click=${async () => {
                  if (!this._form.document) return;

                  const askedFiles = await askForFile({ acceptMultipleFiles: false });
                  if (!askedFiles?.[0]) return;

                  const askedFile = askedFiles[0];
                  this._file = askedFile;
                }}
              >
                ${this._translation.translate('PROMPT', 'upload_document_tmp')}
              </x-button>
            `}

        <x-flex horizontal crossaxis-center space-between space="md" wrap>
          <x-button mode="outline" @click=${() => this.cancel()}>${this._translation.translate('discard')}</x-button>
          <x-button
            @click=${() =>
              XDialog.waitFor(
                this._form.validate(),
                this._translation.translate('WORKSHOP_DOCUMENT', 'validating_workshop_document'),
              )}
          >
            ${this._translation.translate('save')}
          </x-button>
        </x-flex>
      </x-flex>
    `;
  }

  static add(workshopID: string) {
    if (!workshopID) throw new Error('workshop id is required');
    return this.showPopup<Maybe<Appointment>>({
      props: { workshopDocument: new WorkshopDocument({ workshopID }), mode: 'add' },
      options: {
        width: '500px',
      },
    });
  }

  static edit(workshopDocument: WorkshopDocument) {
    return this.showPopup<Maybe<Appointment>>({
      props: { workshopDocument, mode: 'edit' },
      options: {
        width: '500px',
      },
    });
  }

  override async close(): Promise<void> {
    const workshopDocument = this.workshopDocument.value;
    if (!workshopDocument) return;

    await XDialog.waitFor(
      new Promise<void>(async resolve => {
        if (this._file) {
          const fileID = await AdornisFile.upload(this._file);
          workshopDocument.fileID = fileID;
        }

        if (this.mode === 'add') {
          await workshopDocument.create();
        } else if (this.mode === 'edit') {
          await workshopDocument.save();
        }
        resolve();
      }),
      this._translation.translate('WORKSHOP_DOCUMENT', 'saving_workshop_document'),
    );

    if (!workshopDocument.fileID) {
      workshopDocument.file = undefined;
    }

    return super.close();
  }

  override async cancel(returnVal?: any): Promise<void> {
    await super.close();
  }

  override getInnerWrapper(options: Required<DialogOptions>) {
    const wrapperElem = super.getInnerWrapper(options);
    wrapperElem.style.setProperty('background', this.colors.white);
    wrapperElem.style.setProperty('border-radius', this.sizes.borderRadius);
    wrapperElem.style.setProperty('width', `min(550px, 95%)`);
    wrapperElem.style.setProperty('padding', this.spacing.lg);
    return wrapperElem;
  }

  static override get element_name() {
    return 'bh-prompt-workshop-document-modify';
  }
}
